import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Mail, Phone, MessageCircle, Instagram, ArrowUpRight, MapPin, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import PageHeader from '@/components/common/PageHeader';
import Reveal from '@/components/common/Reveal';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { buttonVariants, Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { CONTACT_INFO } from '@/constants/contact';
import { sanitizeInput } from '@/lib/utils';

const budgets = ['Landing Page (₹10k+)', 'Website (₹15k+)', 'Custom / Advanced', 'Not sure yet'];
const categories = ['Dermatology / Skin', 'Dental', 'Aesthetic / Cosmetic', 'Physician / Multi-specialty', 'Hair Clinic', 'Hospitality', 'Nonprofit', 'Other'];

export default function ContactPage() {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', whatsappPref: false, clinic: '', category: '', budget: '', message: '', callTime: '',
  });
  const [sent, setSent] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [timeLeft]);


  const [isSubmitting, setIsSubmitting] = useState(false);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    const { name, phone, email, message } = form;

    // Completely missing everything
    if (!name && !phone && !message) {
      toast.error("Don't be shy! We need a name, a contact number, and a quick note to get started.");
      return;
    }

    // Missing two things
    if (!name && !phone) {
      toast.error("We see your brief, but we have no idea who wrote it or how to reply! Name and number, please.");
      return;
    }
    if (!name && !message) {
      toast.error("We've got your number, but we don't know who we're calling or why! Add a name and a quick note.");
      return;
    }
    if (!phone && !message) {
      toast.error(`Nice to meet you, ${name}. We still need a contact number and a quick brief from you.`);
      return;
    }

    // Missing one thing
    if (!name) {
      toast.error("We have your number and your note, but who are we talking to? A name would be great.");
      return;
    }
    if (!phone) {
      toast.error(`We've got your note, ${name}, but no way to reach you. Please drop your contact number.`);
      return;
    }
    if (!message) {
      toast.error(`Hi ${name}, we have your contact info, but your brief is empty! Tell us what you want to build.`);
      return;
    }

    // Email validation (if provided)
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please provide a valid email address.");
      return;
    }

    // Basic Phone validation (minimum 7 characters, allowing digits, spaces, plus, minus, parentheses)
    if (!/^[\d\s()+-]{7,25}$/.test(phone)) {
      toast.error("Please provide a valid phone number.");
      return;
    }

    setIsSubmitting(true);

    // Sanitize all text inputs
    const sanitizedForm = {
      name: sanitizeInput(form.name),
      email: sanitizeInput(form.email),
      phone: sanitizeInput(form.phone),
      clinic: sanitizeInput(form.clinic),
      message: sanitizeInput(form.message),
      category: form.category,
      budget: form.budget,
      whatsappPref: form.whatsappPref,
      callTime: form.callTime
    };

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          access_key: process.env.REACT_APP_WEB3FORMS_KEY,
          subject: `New Enquiry from ${sanitizedForm.name}`,
          from_name: sanitizedForm.name,
          ...sanitizedForm,
          WhatsApp_Preference: sanitizedForm.whatsappPref ? "Yes, contact me on WhatsApp" : "No preference",
          Best_Time_To_Call: sanitizedForm.callTime || 'Not specified'
        })
      });

      const result = await response.json();

      if (result.success) {
        toast.success('Your note is with us. You\'ll hear back the same day.');
        setSent(true);
        setTimeLeft(60);
      } else {
        toast.error(result.message || 'Something went wrong. Please try emailing us directly.');
      }
    } catch (error) {
      toast.error('Something went wrong. Please try emailing us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.main
      id="main-content"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Helmet>
        <title>Contact | TechsFlow</title>
      </Helmet>
      <PageHeader
        eyebrow="Index · 06 · Contact"
        title="Send a short email."
        italicTail="We'll write back today."
        lede="There is no form marathon here. Five fields, one paragraph, one reply, usually within a working day."
      />

      <section className="py-16 md:py-24">
        <div className="mx-auto container-editorial grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Form */}
          <Reveal className="lg:col-span-7">
            <div className="card-editorial p-6 md:p-10">
              {sent ? (
                <div className="py-16 md:py-20 text-center">
                  <div className="w-14 h-14 rounded-full bg-green-500/10 border border-green-500/30 flex items-center justify-center mx-auto mb-6">
                    <Check className="w-6 h-6 text-green-500" />
                  </div>
                  <h3 className="font-serif text-3xl md:text-4xl mb-3 tracking-tight">Note received.</h3>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    We&rsquo;ll reply from <span className="font-medium text-foreground">{CONTACT_INFO.email}</span>
                    &nbsp; usually the same day, always in a week.
                  </p>
                  <button
                    disabled={timeLeft > 0}
                    onClick={() => { setSent(false); setForm({ name: '', email: '', phone: '', clinic: '', category: '', budget: '', message: '' }); }}
                    className={buttonVariants({ variant: 'ghost-editorial', className: 'mt-8 disabled:opacity-50 disabled:cursor-not-allowed' })}
                  >
                    {timeLeft > 0 ? `Send another in ${timeLeft}s` : 'Send another'}
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-6">
                  <div className="label-eyebrow mb-2">A short brief</div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Your name</Label>
                      <Input id="name" value={form.name} onChange={set('name')} placeholder="Dr. Ananya Rao" maxLength={100} className="h-12 rounded-xl" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Contact Number</Label>
                      <Input id="phone" type="tel" value={form.phone} onChange={set('phone')} placeholder="+91 98765 43210" maxLength={25} className="h-12 rounded-xl" />
                      <div className="flex items-center gap-2 mt-2 pt-1">
                        <Checkbox id="whatsappPref" checked={form.whatsappPref} onCheckedChange={(checked) => setForm(f => ({ ...f, whatsappPref: checked }))} className="data-[state=checked]:bg-sienna data-[state=checked]:text-paper border-border w-3.5 h-3.5 rounded-sm" />
                        <label htmlFor="whatsappPref" className="text-xs text-muted-foreground cursor-pointer">You can also contact me on WhatsApp</label>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Email (Optional)</Label>
                      <Input id="email" type="email" value={form.email} onChange={set('email')} placeholder="you@clinic.com" maxLength={100} className="h-12 rounded-xl" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="clinic" className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Clinic or business</Label>
                      <Input id="clinic" value={form.clinic} onChange={set('clinic')} placeholder="Clinic name (optional)" maxLength={150} className="h-12 rounded-xl" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="category" className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Category</Label>
                      <Select value={form.category} onValueChange={set('category')}>
                        <SelectTrigger id="category" className="h-12 rounded-xl">
                          <SelectValue placeholder="Choose one" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="budget" className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Rough budget</Label>
                      <Select value={form.budget} onValueChange={set('budget')}>
                        <SelectTrigger id="budget" className="h-12 rounded-xl">
                          <SelectValue placeholder="Ballpark" />
                        </SelectTrigger>
                        <SelectContent>
                          {budgets.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-3 pt-2">
                    <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Best time to say hello</Label>
                    <div className="flex flex-wrap gap-2 md:gap-3">
                      {['Morning', 'Afternoon', 'Evening', 'Anytime'].map(time => (
                        <button
                          key={time}
                          type="button"
                          aria-pressed={form.callTime === time}
                          onClick={() => setForm(f => ({ ...f, callTime: time }))}
                          className={`px-4 py-2 text-sm rounded-full border transition-all duration-300 ${form.callTime === time
                              ? 'bg-sienna text-paper border-sienna shadow-sm scale-[1.02]'
                              : 'bg-transparent border-border text-foreground hover:border-sienna/50 hover:bg-sienna/5'
                            }`}
                        >
                          {time}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2 pt-2">
                    <Label htmlFor="message" className="text-xs font-mono uppercase tracking-widest text-muted-foreground">In a paragraph or two</Label>
                    <Textarea id="message" value={form.message} onChange={set('message')} rows={6}
                      placeholder="What are you trying to build? What's the site doing now? Anything you're specifically avoiding?"
                      maxLength={2000}
                      className="rounded-xl" />
                  </div>

                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
                    <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                      Secured by Web3Forms
                    </p>
                    <button type="submit" disabled={isSubmitting} className={buttonVariants({ variant: 'editorial', className: 'disabled:opacity-70 disabled:cursor-not-allowed' })}>
                      {isSubmitting ? (
                        <>Sending... <Loader2 className="w-4 h-4 animate-spin" /></>
                      ) : (
                        <>Send the note <ArrowUpRight className="w-4 h-4" /></>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </Reveal>

          {/* Side card */}
          <Reveal delay={0.15} className="lg:col-span-5 space-y-5">
            <div className="card-editorial p-7">
              <div className="label-eyebrow mb-5">Reach us directly</div>
              <ul className="divide-y divide-border">
                <ContactRow icon={<Mail className="w-4 h-4" />} label="Email" value={CONTACT_INFO.email} href={`mailto:${CONTACT_INFO.email}`} />
                <ContactRow icon={<Phone className="w-4 h-4" />} label="Phone" value={CONTACT_INFO.phone} href={CONTACT_INFO.phoneClean} />
                <ContactRow icon={<MessageCircle className="w-4 h-4" />} label="WhatsApp" value={CONTACT_INFO.phone} href={CONTACT_INFO.whatsapp} />
                <ContactRow icon={<Instagram className="w-4 h-4" />} label="Instagram" value={CONTACT_INFO.instagramHandle} href={CONTACT_INFO.instagram} />
                <ContactRow icon={<MapPin className="w-4 h-4" />} label="Studio" value={CONTACT_INFO.location} />
              </ul>
            </div>

            <div className="card-editorial p-7">
              <div className="label-eyebrow mb-3">Studio hours</div>
              <p className="font-serif text-2xl leading-snug">
                Mon – Fri &nbsp; 10:00 AM – 06:00 PM IST
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                Discovery calls happen on Tuesdays and Thursdays. Emails are answered the same working day.
              </p>
              <div className="mt-5 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest">
                <span className="w-1.5 h-1.5 rounded-full bg-sienna animate-pulse" />
                Currently accepting briefs.
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </motion.main>
  );
}

const ContactRow = ({ icon, label, value, href }) => {
  const inner = (
    <div className="flex items-center gap-4 py-3.5">
      <span className="w-8 h-8 rounded-full border border-border flex items-center justify-center text-muted-foreground">{icon}</span>
      <div className="flex-1">
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className="text-sm md:text-base">{value}</div>
      </div>
      {href && <ArrowUpRight className="w-4 h-4 text-muted-foreground" />}
    </div>
  );
  return (
    <li>
      {href ? (
        <a href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noreferrer" className="block hover:text-sienna transition-colors">
          {inner}
        </a>
      ) : (
        inner
      )}
    </li>
  );
};
